import React, { useEffect, useReducer, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Link,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { createBasket, searchTickers } from '../api';
import { useColorModeValue } from '../components/ui/color-mode';
import { isRecoverableAuthError, getLoginRedirectOptions } from '../utils/auth0Errors';

const initialBasketFormState = {
  title: '',
  description: '',
  tickerQuery: '',
  searchResults: [],
  selectedTickers: [],
  searching: false,
  submitting: false,
  error: '',
  tickerError: '',
};

function basketFormReducer(state, action) {
  switch (action.type) {
    case 'patch':
      return { ...state, ...action.payload };
    case 'addTicker':
      return {
        ...state,
        selectedTickers: [...state.selectedTickers, action.payload],
        tickerQuery: '',
        searchResults: [],
        tickerError: '',
      };
    case 'removeTicker':
      return {
        ...state,
        selectedTickers: state.selectedTickers.filter((item) => item.ticker !== action.payload),
      };
    default:
      return state;
  }
}

function BasketPageFrame({ pageBgStart, pageBgEnd, children }) {
  return (
    <Box
      minH="calc(100vh - var(--nav-h))"
      bgGradient="to-b"
      gradientFrom={pageBgStart}
      gradientTo={pageBgEnd}
    >
      {children}
    </Box>
  );
}

function BasketLoadingState({ pageBgStart, pageBgEnd }) {
  return (
    <BasketPageFrame pageBgStart={pageBgStart} pageBgEnd={pageBgEnd}>
      <Flex justify="center" align="center" py={20}>
        <Spinner size="lg" color="teal.500" />
      </Flex>
    </BasketPageFrame>
  );
}

function BasketCreateIntro({ mutedText }) {
  return (
    <Box>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="0.08em"
        color={mutedText}
      >
        Stock Baskets
      </Text>
      <Heading as="h1" mt={2} fontSize={{ base: '3xl', md: '5xl' }} lineHeight="1.05">
        Create your basket
      </Heading>
      <Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color={mutedText}>
        Pick a title, add the companies you want, and save the basket to your account.
      </Text>
    </Box>
  );
}

function TickerSearchField({
  tickerQuery,
  searchResults,
  searching,
  tickerError,
  searchRef,
  inputBg,
  borderColor,
  dropdownBg,
  dropdownHoverBg,
  mutedText,
  onTickerQueryChange,
  onAddTicker,
}) {
  return (
    <Stack gap={2}>
      <Text fontWeight="semibold">Add tickers</Text>
      <Box ref={searchRef} position="relative">
        <Input
          placeholder="Search by ticker or company name"
          value={tickerQuery}
          onChange={onTickerQueryChange}
          bg={inputBg}
          borderColor={borderColor}
          pr="2.5rem"
        />
        {searching ? (
          <Spinner size="sm" position="absolute" right={3} top="50%" transform="translateY(-50%)" />
        ) : null}

        {tickerQuery.trim() ? (
          <Box
            position="absolute"
            top="calc(100% + 4px)"
            left={0}
            right={0}
            bg={dropdownBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            boxShadow="lg"
            zIndex={10}
            overflow="hidden"
          >
            {searchResults.length > 0 ? (
              <Stack gap={0}>
                {searchResults.map((result) => (
                  <Box
                    key={result.ticker}
                    as="button"
                    type="button"
                    textAlign="left"
                    px={4}
                    py={3}
                    _hover={{ bg: dropdownHoverBg }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onAddTicker(result);
                    }}
                  >
                    <Flex justify="space-between" gap={4}>
                      <Text fontWeight="semibold">{result.ticker}</Text>
                      <Text fontSize="sm" color={mutedText}>
                        {result.name}
                      </Text>
                    </Flex>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box px={4} py={3}>
                <Text fontSize="sm" color={mutedText}>
                  {searching ? 'Searching…' : 'No results'}
                </Text>
              </Box>
            )}
          </Box>
        ) : null}
      </Box>

      {tickerError ? (
        <Text fontSize="sm" color="red.500">
          {tickerError}
        </Text>
      ) : (
        <Text fontSize="sm" color={mutedText}>
          Search the existing `company_tickers` data and add at least one company.
        </Text>
      )}
    </Stack>
  );
}

function SelectedTickerList({
  selectedTickers,
  tickerPillBg,
  tickerPillBorder,
  mutedText,
  onRemoveTicker,
}) {
  return (
    <Stack gap={3}>
      <Text fontWeight="semibold">Selected tickers</Text>
      {selectedTickers.length > 0 ? (
        <Flex wrap="wrap" gap={3}>
          {selectedTickers.map((item) => (
            <Flex
              key={item.ticker}
              align="center"
              gap={3}
              px={4}
              py={2.5}
              bg={tickerPillBg}
              borderWidth="1px"
              borderColor={tickerPillBorder}
              borderRadius="full"
            >
              <Stack gap={0.5}>
                <Text fontWeight="semibold" lineHeight="shorter">
                  {item.ticker}
                </Text>
                <Text fontSize="xs" color={mutedText} lineHeight="short">
                  {item.name || 'Unknown company'}
                </Text>
              </Stack>
              <Button type="button" size="xs" variant="ghost" onClick={() => onRemoveTicker(item.ticker)}>
                Remove
              </Button>
            </Flex>
          ))}
        </Flex>
      ) : (
        <Text color={mutedText}>No tickers added yet.</Text>
      )}
    </Stack>
  );
}

function BasketCreateForm({
  formState,
  searchRef,
  isFormValid,
  panelBg,
  inputBg,
  borderColor,
  mutedText,
  dropdownBg,
  dropdownHoverBg,
  tickerPillBg,
  tickerPillBorder,
  onSubmit,
  onFieldChange,
  onAddTicker,
  onRemoveTicker,
}) {
  const {
    title,
    description,
    tickerQuery,
    searchResults,
    selectedTickers,
    searching,
    submitting,
    error,
    tickerError,
  } = formState;

  return (
    <Box
      as="form"
      onSubmit={onSubmit}
      bg={panelBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      p={{ base: 5, md: 6 }}
      boxShadow="sm"
    >
      <Stack gap={6}>
        {error ? (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>{error}</Alert.Content>
          </Alert.Root>
        ) : null}

        <Stack gap={2}>
          <Text fontWeight="semibold">Title</Text>
          <Input
            placeholder="My Tech Basket"
            value={title}
            onChange={(event) => onFieldChange('title', event.target.value)}
            bg={inputBg}
            borderColor={borderColor}
          />
          <Text fontSize="sm" color={mutedText}>
            Required
          </Text>
        </Stack>

        <Stack gap={2}>
          <Text fontWeight="semibold">Description</Text>
          <Textarea
            placeholder="Optional description"
            value={description}
            onChange={(event) => onFieldChange('description', event.target.value)}
            bg={inputBg}
            borderColor={borderColor}
            minH="120px"
          />
        </Stack>

        <TickerSearchField
          tickerQuery={tickerQuery}
          searchResults={searchResults}
          searching={searching}
          tickerError={tickerError}
          searchRef={searchRef}
          inputBg={inputBg}
          borderColor={borderColor}
          dropdownBg={dropdownBg}
          dropdownHoverBg={dropdownHoverBg}
          mutedText={mutedText}
          onTickerQueryChange={(event) => onFieldChange('tickerQuery', event.target.value)}
          onAddTicker={onAddTicker}
        />

        <SelectedTickerList
          selectedTickers={selectedTickers}
          tickerPillBg={tickerPillBg}
          tickerPillBorder={tickerPillBorder}
          mutedText={mutedText}
          onRemoveTicker={onRemoveTicker}
        />

        <Flex
          justify="space-between"
          align={{ base: 'start', sm: 'center' }}
          direction={{ base: 'column', sm: 'row' }}
          gap={3}
        >
          <Link as={RouterLink} to="/stock-baskets" color="teal.500" fontWeight="semibold">
            Cancel
          </Link>
          <Button type="submit" colorPalette="teal" disabled={!isFormValid || submitting} loading={submitting}>
            Create basket
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
}

function StockBasketCreate() {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [formState, dispatchForm] = useReducer(basketFormReducer, initialBasketFormState);
  const {
    title,
    description,
    tickerQuery,
    selectedTickers,
  } = formState;
  const searchRef = useRef(null);

  const pageBgStart = useColorModeValue('gray.50', 'gray.950');
  const pageBgEnd = useColorModeValue('teal.100', 'gray.900');
  const panelBg = useColorModeValue('white', 'gray.900');
  const inputBg = useColorModeValue('white', 'gray.950');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const dropdownBg = useColorModeValue('white', 'gray.800');
  const dropdownHoverBg = useColorModeValue('teal.50', 'whiteAlpha.100');
  const tickerPillBg = useColorModeValue('teal.50', 'teal.900');
  const tickerPillBorder = useColorModeValue('teal.200', 'teal.700');

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      void loginWithRedirect({
        appState: { returnTo: '/stock-baskets/new' },
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  useEffect(() => {
    if (!isAuthenticated || !tickerQuery.trim()) {
      dispatchForm({ type: 'patch', payload: { searchResults: [], searching: false } });
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      dispatchForm({ type: 'patch', payload: { searching: true, tickerError: '' } });
      try {
        const data = await searchTickers(tickerQuery);
        if (!cancelled) {
          dispatchForm({ type: 'patch', payload: { searchResults: data.results || [] } });
        }
      } catch (err) {
        if (!cancelled) {
          dispatchForm({
            type: 'patch',
            payload: { searchResults: [], tickerError: err.message || 'Failed to search tickers' },
          });
        }
      } finally {
        if (!cancelled) {
          dispatchForm({ type: 'patch', payload: { searching: false } });
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [isAuthenticated, tickerQuery]);

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        dispatchForm({ type: 'patch', payload: { searchResults: [] } });
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const handleAddTicker = (result) => {
    const ticker = result?.ticker?.toUpperCase();
    if (!ticker) {
      dispatchForm({ type: 'patch', payload: { tickerError: 'Select a valid ticker from the search results.' } });
      return;
    }

    if (selectedTickers.some((item) => item.ticker === ticker)) {
      dispatchForm({ type: 'patch', payload: { tickerError: `${ticker} is already in the basket.` } });
      return;
    }

    dispatchForm({
      type: 'addTicker',
      payload: {
        ticker,
        name: result.name || '',
        sector: result.sector || '',
      },
    });
  };

  const handleRemoveTicker = (tickerToRemove) => {
    dispatchForm({ type: 'removeTicker', payload: tickerToRemove });
  };

  const handleFieldChange = (field, value) => {
    dispatchForm({ type: 'patch', payload: { [field]: value } });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatchForm({ type: 'patch', payload: { error: '' } });

    if (!title.trim()) {
      dispatchForm({ type: 'patch', payload: { error: 'Title is required.' } });
      return;
    }

    if (selectedTickers.length === 0) {
      dispatchForm({
        type: 'patch',
        payload: { error: 'Add at least one ticker before creating the basket.' },
      });
      return;
    }

    try {
      dispatchForm({ type: 'patch', payload: { submitting: true } });
      const accessToken = await getAccessTokenSilently();
      const created = await createBasket(accessToken, {
        title: title.trim(),
        description: description.trim(),
        tickers: selectedTickers.map((item) => item.ticker),
      });
      navigate(`/stock-baskets/${created.id}`);
    } catch (err) {
      if (isRecoverableAuthError(err)) {
        await loginWithRedirect(getLoginRedirectOptions());
        return;
      }
      dispatchForm({ type: 'patch', payload: { error: err.message || 'Failed to create basket' } });
    } finally {
      dispatchForm({ type: 'patch', payload: { submitting: false } });
    }
  };

  const isFormValid = title.trim().length > 0 && selectedTickers.length > 0;

  if (isLoading || !isAuthenticated) {
    return <BasketLoadingState pageBgStart={pageBgStart} pageBgEnd={pageBgEnd} />;
  }

  return (
    <BasketPageFrame pageBgStart={pageBgStart} pageBgEnd={pageBgEnd}>
      <Box maxW="900px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 8, md: 10 }}>
        <Stack gap={8}>
          <BasketCreateIntro mutedText={mutedText} />
          <BasketCreateForm
            formState={formState}
            searchRef={searchRef}
            isFormValid={isFormValid}
            panelBg={panelBg}
            inputBg={inputBg}
            borderColor={borderColor}
            mutedText={mutedText}
            dropdownBg={dropdownBg}
            dropdownHoverBg={dropdownHoverBg}
            tickerPillBg={tickerPillBg}
            tickerPillBorder={tickerPillBorder}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            onAddTicker={handleAddTicker}
            onRemoveTicker={handleRemoveTicker}
          />
        </Stack>
      </Box>
    </BasketPageFrame>
  );
}

export default StockBasketCreate;
